import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, fmtDateTime } from "@/lib/api";
import { LEAD_TYPE_LABELS, STATUS_LABELS } from "@/constants";

export const Stat = ({ label, value, testid }) => (
  <div className="bg-surface border border-white/10 rounded-sm p-5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
    <p className="font-serif text-4xl text-white mt-2">{value ?? "–"}</p>
  </div>
);

export const Badge = ({ children, tone = "neutral" }) => {
  const tones = { neutral: "bg-white/5 text-white/70 border-white/10", gold: "bg-gold/10 text-gold border-gold/30", green: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", red: "bg-red-500/10 text-red-300 border-red-500/30", blue: "bg-sky-500/10 text-sky-300 border-sky-500/30" };
  return <span className={`inline-block text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border ${tones[tone]}`}>{children}</span>;
};

export const statusTone = (s) => ({ new: "gold", contacted: "blue", qualified: "blue", quoted: "blue", won: "green", lost: "red" }[s] || "neutral");

const Breakdown = ({ title, data, labels, linkKey }) => (
  <div className="bg-surface border border-white/10 rounded-sm p-5">
    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">{title}</p>
    <ul className="space-y-2 text-sm">
      {Object.entries(data || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <li key={k} className="flex justify-between"><Link to={`/admin/leads?${linkKey}=${k}`} className="text-white/70 hover:text-gold capitalize">{labels?.[k] || k}</Link><span className="text-white">{v}</span></li>
      ))}
      {!Object.keys(data || {}).length && <li className="text-white/30">No data yet</li>}
    </ul>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi("/admin/dashboard").then(setD).catch(() => {}); }, []);
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl lg:text-4xl text-white">Dashboard</h1>
      <p className="text-sm text-white/50 mt-1">Every enquiry, in one place.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat label="Today" value={d?.today} testid="stat-today" />
        <Stat label="This week" value={d?.week} testid="stat-week" />
        <Stat label="All leads" value={d?.total} testid="stat-total" />
        <Stat label="Active members" value={d?.members} testid="stat-members" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Breakdown title="By type" data={d?.by_type} labels={LEAD_TYPE_LABELS} linkKey="type" />
        <Breakdown title="By source" data={d?.by_source} linkKey="source" />
        <Breakdown title="By pipeline stage" data={d?.by_status} labels={STATUS_LABELS} linkKey="status" />
      </div>
      {d?.follow_ups_due?.length > 0 && (
        <div className="mt-8 bg-gold/5 border border-gold/30 rounded-sm p-5" data-testid="follow-ups-due">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-3">Follow-ups due</p>
          <ul className="space-y-2 text-sm">{d.follow_ups_due.map((l) => (<li key={l.id}><Link to={`/admin/leads/${l.id}`} className="text-white hover:text-gold">{l.name}</Link> <span className="text-white/40">· {LEAD_TYPE_LABELS[l.type]} · {fmtDateTime(l.follow_up_at)}</span></li>))}</ul>
        </div>
      )}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4"><p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Recent leads</p><Link to="/admin/leads" className="text-xs text-gold hover:text-gold-light" data-testid="dashboard-all-leads-link">All leads →</Link></div>
        <div className="bg-surface border border-white/10 rounded-sm divide-y divide-white/5">
          {(d?.recent || []).map((l) => (
            <Link key={l.id} to={`/admin/leads/${l.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/5 transition-colors" data-testid={`recent-lead-${l.id}`}>
              <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{l.name} <span className="text-white/40">· {l.email}</span></p><p className="text-xs text-white/40 truncate">{l.reference} · {l.details?.summary || l.message || ""}</p></div>
              <Badge tone="gold">{LEAD_TYPE_LABELS[l.type]}</Badge><Badge tone={statusTone(l.status)}>{STATUS_LABELS[l.status]}</Badge>
              <span className="text-xs text-white/40 whitespace-nowrap hidden sm:block">{fmtDateTime(l.created_at)}</span>
            </Link>
          ))}
          {d && !d.recent?.length && <p className="px-5 py-6 text-sm text-white/40">No leads yet.</p>}
        </div>
      </div>
      {d && !d.perplexity_available && <p className="mt-8 text-xs text-white/30">Trip & asset research becomes available once the Perplexity key is added.</p>}
    </div>
  );
}
